import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import {
  getOrganizationSalesPeriods,
  getOrganizationSalesActivity,
  getOrganizationSalesProducts,
  getOrganizationSalesSummary,
  getProductGroups
} from '../../../../api/admin/reports';
import { ACL } from '../../../../config/constants';
import { history as applicationHistory } from '../../../../store/store';
import { ReportFilters } from '../../../../pages/reports/components/ReportFilters';
import { SummaryReport } from '../../../../pages/reports/components/SummaryReport';
import { ProductsReport } from '../../../../pages/reports/components/ProductsReport';
import { PeriodsReport } from '../../../../pages/reports/components/PeriodsReport';
import { ReportPeriodContext } from '../../../../pages/reports/components/ReportPeriodContext';
import { ActivityReport } from '../../../../pages/reports/components/ActivityReport';
import {
  parseReportQuery,
  reportFiltersEqual,
  serializeReportQuery,
  validateReportFilters
} from '../../../../pages/reports/reportFilters';
import {
  catalogEntityIdentifiers,
  isArchiveProductGroup,
  resolveCatalogLabel,
  resolveProductGroupSection,
  resolveProductCatalogLabel
} from '../../../../pages/reports/reportCatalogLabels';
import { reconcileProductCatalogFilters } from '../../../../pages/reports/reportProductCascade';
import '../../../../pages/reports/ListReports.scss';
import './OrganizationReportsTab.scss';

const ORGANIZATION_REPORT_TABS = ['summary', 'products', 'periods', 'activity'];

function organizationScopedFilters(filters) {
  return {
    ...filters,
    organizationMode: 'all',
    organizationIds: []
  };
}

function parseOrganizationReportQuery(search) {
  const parsed = parseReportQuery(search);

  return {
    activeTab: ORGANIZATION_REPORT_TABS.includes(parsed.activeTab) ? parsed.activeTab : 'summary',
    filters: organizationScopedFilters(parsed.filters),
    periodLens: parsed.periodLens,
    productTable: parsed.productTable
  };
}

function serializeOrganizationReportQuery(filters, activeTab, productTable, periodLens) {
  const params = new URLSearchParams(serializeReportQuery(
    organizationScopedFilters(filters),
    activeTab,
    productTable,
    periodLens
  ));

  params.set('tab', 'reports');
  params.delete('organization_mode');
  params.delete('organization_ids');
  params.delete('organization_ids[]');
  params.delete('organization_page');
  params.delete('organization_per_page');
  params.delete('organization_sort_by');
  params.delete('organization_sort_direction');

  return `?${params.toString()}`;
}

function normalizeProductCatalog(productGroupsResponse, locale = 'ro', formatMessage) {
  const productGroups = Array.isArray(productGroupsResponse && productGroupsResponse.data)
    ? productGroupsResponse.data
    : [];
  const collator = new Intl.Collator(locale, { sensitivity: 'base' });
  const groups = productGroups.map(group => {
    if (!group || !group.id) {
      return null;
    }

    const archive = isArchiveProductGroup(group);
    const section = resolveProductGroupSection(group, locale, formatMessage);
    return {
      archive,
      categoryKey: section.key,
      categoryLabel: section.label,
      categoryRank: section.rank,
      entity: group,
      identifiers: catalogEntityIdentifiers(group),
      label: resolveCatalogLabel(group, locale, formatMessage),
      value: String(group.id)
    };
  }).filter(Boolean).sort((left, right) => (
    left.categoryRank - right.categoryRank
      || collator.compare(left.label, right.label)
      || left.value.localeCompare(right.value)
  ));
  const products = groups.reduce((options, group) => [
    ...options,
    ...(Array.isArray(group.entity.products) ? group.entity.products.map(product => ({
      archive: group.archive,
      categoryKey: group.categoryKey,
      categoryLabel: group.categoryLabel,
      categoryRank: group.categoryRank,
      groupId: group.value,
      groupLabel: group.label,
      identifiers: catalogEntityIdentifiers(product),
      label: resolveCatalogLabel(product, locale, formatMessage),
      selectedLabel: resolveProductCatalogLabel(product, group.entity, locale, formatMessage),
      value: String(product.id)
    })) : [])
  ], []).sort((left, right) => (
    left.categoryRank - right.categoryRank
      || collator.compare(left.groupLabel, right.groupLabel)
      || collator.compare(left.label, right.label)
      || left.value.localeCompare(right.value)
  ));

  return {
    organizations: [],
    productGroups: groups.map(({ entity, categoryRank, ...group }) => group),
    products: products.map(({ categoryRank, ...product }) => product)
  };
}

function reportTableEqual(left, right) {
  return left.page === right.page
    && left.perPage === right.perPage
    && left.sortBy === right.sortBy
    && left.sortDirection === right.sortDirection;
}

function OrganizationReportsTab({ location, navigationHistory, organizationId }) {
  const intl = useIntl();
  const hasAccess = ACL.CLIENT.REPORTS.LIST.hasAccess();
  const locationSearch = location && location.search;
  const initialQuery = useMemo(
    () => parseOrganizationReportQuery(locationSearch),
    // The detail route owns this instance; external URL changes are reconciled below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );
  const [activeTab, setActiveTab] = useState(initialQuery.activeTab);
  const [periodLens, setPeriodLens] = useState(initialQuery.periodLens);
  const [comparisonVisibility, setComparisonVisibility] = useState({
    financial: true,
    products: true
  });
  const [appliedFilters, setAppliedFilters] = useState(initialQuery.filters);
  const [draftFilters, setDraftFilters] = useState(initialQuery.filters);
  const [productTable, setProductTable] = useState(initialQuery.productTable);
  const [catalogResponse, setCatalogResponse] = useState(null);
  const [catalogLoading, setCatalogLoading] = useState(hasAccess && Boolean(organizationId));
  const [catalogReady, setCatalogReady] = useState(false);
  const [summary, setSummary] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(hasAccess && Boolean(organizationId) && initialQuery.activeTab === 'summary');
  const [summaryError, setSummaryError] = useState(null);
  const [products, setProducts] = useState(null);
  const [productsLoading, setProductsLoading] = useState(hasAccess && Boolean(organizationId) && initialQuery.activeTab === 'products');
  const [productsError, setProductsError] = useState(null);
  const [periods, setPeriods] = useState(null);
  const [periodsLoading, setPeriodsLoading] = useState(hasAccess && Boolean(organizationId) && initialQuery.activeTab === 'periods');
  const [periodsError, setPeriodsError] = useState(null);
  const [activity, setActivity] = useState(null);
  const [activityLoading, setActivityLoading] = useState(hasAccess && Boolean(organizationId) && initialQuery.activeTab === 'activity');
  const [activityError, setActivityError] = useState(null);
  const summaryAbortRef = useRef(null);
  const summaryDataKeyRef = useRef(null);
  const productsAbortRef = useRef(null);
  const periodsAbortRef = useRef(null);
  const activityAbortRef = useRef(null);
  const lastLocationSearchRef = useRef(locationSearch);
  const generatedId = useId().replace(/:/g, '');
  const catalogs = useMemo(() => normalizeProductCatalog(
    catalogResponse,
    intl.locale || 'ro',
    intl.formatMessage
  ), [catalogResponse, intl.formatMessage, intl.locale]);
  const history = navigationHistory || applicationHistory;

  const updateUrl = useCallback((tab, filters, table = productTable, lens = periodLens) => {
    const search = serializeOrganizationReportQuery(filters, tab, table, lens);
    if (history && history.push) {
      history.push({
        pathname: location && location.pathname,
        search
      });
    }
  }, [history, location, periodLens, productTable]);

  const loadSummary = useCallback(filters => {
    if (!hasAccess || !organizationId) {
      return;
    }
    if (summaryAbortRef.current) {
      summaryAbortRef.current.abort();
    }
    const controller = new AbortController();
    summaryAbortRef.current = controller;
    setSummaryLoading(true);
    setSummaryError(null);
    const requestKey = JSON.stringify(filters);
    getOrganizationSalesSummary(organizationId, filters, controller.signal)
      .then(response => {
        if (!controller.signal.aborted) {
          setSummary(response && response.data ? response.data : null);
          summaryDataKeyRef.current = requestKey;
        }
      })
      .catch(error => {
        if (!controller.signal.aborted && error.code !== 'ERR_CANCELED') {
          setSummaryError(error);
          setSummary(null);
          summaryDataKeyRef.current = null;
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) {
          setSummaryLoading(false);
        }
      });
  }, [hasAccess, organizationId]);

  const loadProducts = useCallback((filters, table) => {
    if (!hasAccess || !organizationId) {
      return;
    }
    if (productsAbortRef.current) {
      productsAbortRef.current.abort();
    }
    const controller = new AbortController();
    productsAbortRef.current = controller;
    setProductsLoading(true);
    setProductsError(null);
    getOrganizationSalesProducts(organizationId, filters, table, controller.signal)
      .then(response => {
        if (!controller.signal.aborted) {
          setProducts(response && response.data ? response.data : null);
        }
      })
      .catch(error => {
        if (!controller.signal.aborted && error.code !== 'ERR_CANCELED') {
          setProductsError(error);
          setProducts(null);
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) {
          setProductsLoading(false);
        }
      });
  }, [hasAccess, organizationId]);

  const loadPeriods = useCallback(filters => {
    if (!hasAccess || !organizationId) {
      return;
    }
    if (periodsAbortRef.current) {
      periodsAbortRef.current.abort();
    }
    const controller = new AbortController();
    periodsAbortRef.current = controller;
    setPeriodsLoading(true);
    setPeriodsError(null);
    getOrganizationSalesPeriods(organizationId, filters, controller.signal)
      .then(response => {
        if (!controller.signal.aborted) {
          setPeriods(response && response.data ? response.data : null);
        }
      })
      .catch(error => {
        if (!controller.signal.aborted && error.code !== 'ERR_CANCELED') {
          setPeriodsError(error);
          setPeriods(null);
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) {
          setPeriodsLoading(false);
        }
      });
  }, [hasAccess, organizationId]);

  const loadActivity = useCallback(filters => {
    if (!hasAccess || !organizationId) return;
    if (activityAbortRef.current) activityAbortRef.current.abort();
    const controller = new AbortController();
    activityAbortRef.current = controller;
    setActivityLoading(true);
    setActivityError(null);
    getOrganizationSalesActivity(organizationId, filters, controller.signal)
      .then(response => {
        if (!controller.signal.aborted) setActivity(response && response.data ? response.data : null);
      })
      .catch(error => {
        if (!controller.signal.aborted && error.code !== 'ERR_CANCELED') {
          setActivityError(error);
          setActivity(null);
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) setActivityLoading(false);
      });
  }, [hasAccess, organizationId]);

  useEffect(() => {
    if (!hasAccess || !organizationId) {
      return undefined;
    }
    let mounted = true;
    setCatalogLoading(true);
    getProductGroups({ disablePagination: true })
      .then(response => {
        if (mounted) {
          setCatalogResponse(response);
          setCatalogReady(Boolean(response && Array.isArray(response.data)));
        }
      })
      .catch(() => {
        // Product filters remain optional. Keep date and order-type reporting
        // usable if the catalogue is temporarily unavailable.
      })
      .finally(() => {
        if (mounted) {
          setCatalogLoading(false);
        }
      });

    return () => {
      mounted = false;
    };
  }, [hasAccess, organizationId]);

  useEffect(() => {
    if (activeTab !== 'summary') {
      return undefined;
    }
    loadSummary(appliedFilters);
    return () => summaryAbortRef.current && summaryAbortRef.current.abort();
  }, [activeTab, appliedFilters, loadSummary]);

  useEffect(() => {
    if (activeTab !== 'periods'
      || summaryDataKeyRef.current === JSON.stringify(appliedFilters)) return undefined;
    loadSummary(appliedFilters);
    return () => summaryAbortRef.current && summaryAbortRef.current.abort();
  }, [activeTab, appliedFilters, loadSummary]);

  useEffect(() => {
    if (activeTab !== 'products') {
      return undefined;
    }
    loadProducts(appliedFilters, productTable);
    return () => productsAbortRef.current && productsAbortRef.current.abort();
  }, [activeTab, appliedFilters, loadProducts, productTable]);

  useEffect(() => {
    if (activeTab !== 'periods') {
      return undefined;
    }
    loadPeriods(appliedFilters);
    return () => periodsAbortRef.current && periodsAbortRef.current.abort();
  }, [activeTab, appliedFilters, loadPeriods]);

  useEffect(() => {
    if (activeTab !== 'activity') return undefined;
    loadActivity(appliedFilters);
    return () => activityAbortRef.current && activityAbortRef.current.abort();
  }, [activeTab, appliedFilters, loadActivity]);

  useEffect(() => {
    if (locationSearch === lastLocationSearchRef.current) {
      return;
    }
    lastLocationSearchRef.current = locationSearch;
    const parsed = parseOrganizationReportQuery(locationSearch);
    setActiveTab(parsed.activeTab);
    setPeriodLens(parsed.periodLens);
    if (!reportTableEqual(parsed.productTable, productTable)) {
      setProductTable(parsed.productTable);
    }
    if (!reportFiltersEqual(parsed.filters, appliedFilters)) {
      setAppliedFilters(parsed.filters);
      setDraftFilters(parsed.filters);
    }
  }, [appliedFilters, locationSearch, productTable]);

  useEffect(() => {
    if (!catalogReady) {
      return;
    }
    const nextApplied = organizationScopedFilters(reconcileProductCatalogFilters(
      appliedFilters,
      catalogs.products,
      catalogs.productGroups
    ));
    const nextDraft = organizationScopedFilters(reconcileProductCatalogFilters(
      draftFilters,
      catalogs.products,
      catalogs.productGroups
    ));
    if (!reportFiltersEqual(nextApplied, appliedFilters)) {
      setAppliedFilters(nextApplied);
      updateUrl(activeTab, nextApplied, productTable, periodLens);
    }
    if (!reportFiltersEqual(nextDraft, draftFilters)) {
      setDraftFilters(nextDraft);
    }
  }, [
    activeTab,
    appliedFilters,
    catalogReady,
    catalogs.productGroups,
    catalogs.products,
    draftFilters,
    periodLens,
    productTable,
    updateUrl
  ]);

  if (!hasAccess) {
    return null;
  }

  const validationError = validateReportFilters(draftFilters);
  const dirty = !reportFiltersEqual(draftFilters, appliedFilters);
  const filters = (
    <ReportFilters
      catalogLoading={catalogLoading}
      dirty={dirty}
      filters={draftFilters}
      onApply={() => {
        if (validationError) {
          return;
        }
        const nextTable = { ...productTable, page: 1 };
        const nextFilters = organizationScopedFilters(draftFilters);
        setAppliedFilters(nextFilters);
        setProductTable(nextTable);
        updateUrl(activeTab, nextFilters, nextTable, periodLens);
      }}
      onChange={filters => setDraftFilters(organizationScopedFilters(filters))}
      onReset={() => setDraftFilters(organizationScopedFilters(parseReportQuery('').filters))}
      options={catalogs}
      reportMode={activeTab === 'activity' ? 'activity' : 'sales'}
      showOrganizations={false}
      validationError={validationError}
    />
  );
  const reportPanels = {
    summary: (
      <>
        {filters}
        <ReportPeriodContext hidden={summaryLoading || Boolean(summaryError)} meta={summary && summary.meta}/>
          <SummaryReport
            comparisonMode={appliedFilters.compare}
            data={summary}
            error={summaryError}
            financialComparisonEnabled={comparisonVisibility.financial}
            loading={summaryLoading}
            onFinancialComparisonChange={enabled => setComparisonVisibility(current => ({
              ...current,
              financial: enabled
            }))}
            onProductComparisonChange={enabled => setComparisonVisibility(current => ({
              ...current,
              products: enabled
            }))}
            onRetry={() => loadSummary(appliedFilters)}
            periodPreset={appliedFilters.preset}
            productComparisonEnabled={comparisonVisibility.products}
          />
      </>
    ),
    products: (
      <>
        {filters}
        <ReportPeriodContext hidden={productsLoading || Boolean(productsError)} meta={products && products.meta}/>
        <ProductsReport
          data={products}
          error={productsError}
          loading={productsLoading}
          onRetry={() => loadProducts(appliedFilters, productTable)}
          onTableChange={table => {
            setProductTable(table);
            updateUrl('products', appliedFilters, table, periodLens);
          }}
          table={productTable}
        />
      </>
    ),
    periods: (
      <>
        {filters}
        <ReportPeriodContext hidden={periodsLoading || Boolean(periodsError)} meta={periods && periods.meta}/>
        <PeriodsReport
          activityData={periods && periods.activity}
          activityError={periodsError}
          activityLoading={periodsLoading}
          data={periods}
          error={periodsError}
          lens={periodLens}
          loading={periodsLoading}
          onLensChange={lens => {
            setPeriodLens(lens);
            updateUrl('periods', appliedFilters, productTable, lens);
          }}
          onRetry={() => loadPeriods(appliedFilters)}
          periodPreset={appliedFilters.preset}
          purchaseData={summary}
          purchaseError={summaryError}
          purchaseLoading={summaryLoading || summaryDataKeyRef.current !== JSON.stringify(appliedFilters)}
        />
      </>
    ),
    activity: (
      <>
        {filters}
        <ReportPeriodContext hidden={activityLoading || Boolean(activityError)} meta={activity && activity.meta}/>
        <ActivityReport
          data={activity}
          error={activityError}
          loading={activityLoading}
          onRetry={() => loadActivity(appliedFilters)}
          scoped
        />
      </>
    )
  };
  const handleTabsKeyDown = event => {
    const buttons = Array.from(event.currentTarget.querySelectorAll('[role="tab"]'));
    const current = buttons.indexOf(document.activeElement);
    let next;

    if (event.key === 'Home') {
      next = 0;
    } else if (event.key === 'End') {
      next = buttons.length - 1;
    } else if (event.key === 'ArrowRight') {
      next = (current + 1) % buttons.length;
    } else if (event.key === 'ArrowLeft') {
      next = (current - 1 + buttons.length) % buttons.length;
    } else {
      return;
    }

    event.preventDefault();
    buttons[next].focus();
  };

  return (
    <section
      aria-label={intl.formatMessage({ id: 'navigation.reports' })}
      className="organization-reports"
      data-organization-id={organizationId}
    >
      <div
        aria-label={intl.formatMessage({ id: 'components.pages.reports.organization.sections' })}
        className="organization-reports__tabs"
        onKeyDown={handleTabsKeyDown}
        role="tablist"
      >
        {ORGANIZATION_REPORT_TABS.map(tab => (
          <button
            aria-controls={`organization-reports-${generatedId}-panel`}
            aria-selected={activeTab === tab}
            className={activeTab === tab ? 'active' : ''}
            id={`organization-reports-${generatedId}-tab-${tab}`}
            key={tab}
            onClick={() => {
              setActiveTab(tab);
              updateUrl(tab, appliedFilters, productTable, periodLens);
            }}
            role="tab"
            tabIndex={activeTab === tab ? 0 : -1}
            type="button"
          >
            <FormattedMessage id={`components.pages.reports.tabs.${tab}`}/>
          </button>
        ))}
      </div>
      <div
        aria-labelledby={`organization-reports-${generatedId}-tab-${activeTab}`}
        className="organization-reports__panel"
        id={`organization-reports-${generatedId}-panel`}
        role="tabpanel"
      >
        {reportPanels[activeTab]}
      </div>
    </section>
  );
}

export {
  normalizeProductCatalog,
  OrganizationReportsTab,
  organizationScopedFilters,
  parseOrganizationReportQuery,
  serializeOrganizationReportQuery
};
